import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Types
export interface StudentVacationInfo {
  id: number;
  dateFrom: string;
  dateTo: string;
  vacationType?: { id: number; name: string; isFreeze?: boolean };
}

export interface Student {
  id: number;
  firstName: string;
  lastName: string;
  surname?: string;
  contacts?: string;
  startDate?: string | null;
  quitDate?: string | null;
  /** Date the student joined the fetched group (group_students.attributed_on) */
  attributedOn?: string | null;
  vacations?: StudentVacationInfo[];
}

export interface Absence {
  id: number;
  studentId: number;
  student: {
    id: number;
    firstName: string;
    lastName: string;
    surname?: string;
    quitDate?: string | null;
  };
}

export interface Lesson {
  id: number;
  groupId: number;
  date: string;
  topic?: string;
  link?: string;
  canceled?: boolean;
  group?: {
    id: number;
    name?: string;
    level: string;
  };
  vocabulary?: {
    id: number;
    word: string;
  }[];
  absences?: Absence[];
  createdAt: string;
  updatedAt: string;
}

export interface LessonFormData {
  groupId: number;
  date: string;
  topic?: string;
  link?: string;
  canceled?: boolean;
  absentStudentIds?: number[];
}

export interface LessonsState {
  lessons: Lesson[];
  currentLesson: Lesson | null;
  students: Student[];
  loadingStudents: boolean;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
    groupId?: number;
    level?: string;
    dateFrom?: string;
    dateTo?: string;
    canceled?: boolean;
  };
}

// Initial state
const initialState: LessonsState = {
  lessons: [],
  currentLesson: null,
  students: [],
  loadingStudents: false,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  },
  filters: {
    search: '',
    groupId: undefined,
    level: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    canceled: undefined,
  },
};

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks
export const fetchLessonsAsync = createAsyncThunk(
  'lessons/fetchLessons',
  async (params: { date?: string; dateFrom?: string; dateTo?: string; groupId?: number; canceled?: boolean; page?: number; limit?: number } = {}, { rejectWithValue, getState }) => {
    try {
      const { date, dateFrom, dateTo, groupId, canceled, page = 1, limit = 50 } = params;
      const queryParams = new URLSearchParams();

      if (date) {
        queryParams.append('date', date);
      }
      if (dateFrom) {
        queryParams.append('dateFrom', dateFrom);
      }
      if (dateTo) {
        queryParams.append('dateTo', dateTo);
      }
      if (groupId) {
        queryParams.append('group', groupId.toString());
      }
      if (canceled !== undefined) {
        queryParams.append('canceled', canceled.toString());
      }
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
      
      const response = await axios.get(`${API_BASE_URL}/lessons?${queryParams}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch lessons'));
    }
  }
);

// Keep the old function for backward compatibility
export const fetchLessonsByDateAsync = createAsyncThunk(
  'lessons/fetchLessonsByDate',
  async (params: { date: string; groupId?: number }, { rejectWithValue, getState }) => {
    try {
      const { date, groupId } = params;
      const queryParams = new URLSearchParams({ date });
      
      if (groupId) {
        queryParams.append('group', groupId.toString());
      }
      
      const response = await axios.get(`${API_BASE_URL}/lessons?${queryParams}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch lessons'));
    }
  }
);

export const fetchLessonByIdAsync = createAsyncThunk(
  'lessons/fetchLessonById',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/lessons/${id}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.lesson;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch lesson'));
    }
  }
);

export const createLessonAsync = createAsyncThunk(
  'lessons/createLesson',
  async (lessonData: LessonFormData, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/lessons`, lessonData, {
        headers: getAuthHeaders(getState)
      });
      return response.data.lesson;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create lesson'));
    }
  }
);

export const updateLessonAsync = createAsyncThunk(
  'lessons/updateLesson',
  async ({ id, data }: { id: number; data: Partial<LessonFormData> }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/lessons/${id}`, data, {
        headers: getAuthHeaders(getState)
      });
      return response.data.lesson;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update lesson'));
    }
  }
);

export const addVocabularyAsync = createAsyncThunk(
  'lessons/addVocabulary',
  async ({ lessonId, words }: { lessonId: number; words: string[] }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/lessons/${lessonId}/vocabulary`, { words }, {
        headers: getAuthHeaders(getState)
      });
      return { lessonId, vocabulary: response.data.vocabulary };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to add vocabulary'));
    }
  }
);

export const deleteVocabularyAsync = createAsyncThunk(
  'lessons/deleteVocabulary',
  async (vocabularyId: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/lessons/vocabulary/${vocabularyId}`, {
        headers: getAuthHeaders(getState)
      });
      return vocabularyId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete vocabulary'));
    }
  }
);

export const deleteLessonAsync = createAsyncThunk(
  'lessons/deleteLesson',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/lessons/${id}`, {
        headers: getAuthHeaders(getState)
      });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete lesson'));
    }
  }
);

export const fetchGroupStudentsAsync = createAsyncThunk(
  'lessons/fetchGroupStudents',
  async (groupId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/groups/${groupId}/students`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.students;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch students'));
    }
  }
);

const lessonsSlice = createSlice({
  name: 'lessons',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentLesson: (state) => {
      state.currentLesson = null;
    },
    clearStudents: (state) => {
      state.students = [];
    },
    setFilters: (state, action: PayloadAction<Partial<LessonsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch lessons with optional filters
      .addCase(fetchLessonsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLessonsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.lessons = action.payload.lessons;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
        state.error = null;
      })
      .addCase(fetchLessonsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch lessons by date (backward compatibility)
      .addCase(fetchLessonsByDateAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLessonsByDateAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.lessons = action.payload.lessons;
        state.error = null;
      })
      .addCase(fetchLessonsByDateAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch lesson by ID
      .addCase(fetchLessonByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLessonByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLesson = action.payload;
        state.error = null;
      })
      .addCase(fetchLessonByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create lesson
      .addCase(createLessonAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLessonAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.lessons.push(action.payload);
        state.error = null;
      })
      .addCase(createLessonAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update lesson
      .addCase(updateLessonAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLessonAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLesson = action.payload;
        const index = state.lessons.findIndex(lesson => lesson.id === action.payload.id);
        if (index !== -1) {
          state.lessons[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateLessonAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add vocabulary
      .addCase(addVocabularyAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addVocabularyAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { lessonId, vocabulary } = action.payload;
        
        // Update current lesson if it matches
        if (state.currentLesson && state.currentLesson.id === lessonId) {
          state.currentLesson.vocabulary = [
            ...(state.currentLesson.vocabulary || []),
            ...vocabulary
          ];
        }
        
        // Update lesson in lessons array
        const lessonIndex = state.lessons.findIndex(lesson => lesson.id === lessonId);
        if (lessonIndex !== -1) {
          state.lessons[lessonIndex].vocabulary = [
            ...(state.lessons[lessonIndex].vocabulary || []),
            ...vocabulary
          ];
        }
        
        state.error = null;
      })
      .addCase(addVocabularyAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete vocabulary
      .addCase(deleteVocabularyAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVocabularyAsync.fulfilled, (state, action) => {
        state.loading = false;
        const vocabularyId = action.payload;
        
        // Remove from current lesson
        if (state.currentLesson && state.currentLesson.vocabulary) {
          state.currentLesson.vocabulary = state.currentLesson.vocabulary.filter(
            vocab => vocab.id !== vocabularyId
          );
        }
        
        // Remove from lessons array
        state.lessons.forEach(lesson => {
          if (lesson.vocabulary) {
            lesson.vocabulary = lesson.vocabulary.filter(vocab => vocab.id !== vocabularyId);
          }
        });
        
        state.error = null;
      })
      .addCase(deleteVocabularyAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete lesson
      .addCase(deleteLessonAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLessonAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.lessons = state.lessons.filter(lesson => lesson.id !== action.payload);
        state.pagination.totalItems = Math.max(0, state.pagination.totalItems - 1);
        state.error = null;
      })
      .addCase(deleteLessonAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch group students
      .addCase(fetchGroupStudentsAsync.pending, (state) => {
        state.loadingStudents = true;
        state.error = null;
      })
      .addCase(fetchGroupStudentsAsync.fulfilled, (state, action) => {
        state.loadingStudents = false;
        state.students = action.payload;
        state.error = null;
      })
      .addCase(fetchGroupStudentsAsync.rejected, (state, action) => {
        state.loadingStudents = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentLesson, clearStudents, setFilters, setPage, clearFilters } = lessonsSlice.actions;
export default lessonsSlice.reducer; 